// Dashboard API client. The venue token is stored in localStorage and sent as
// a bearer; the tenant is derived from the token server-side.
//
// The same login also signs in the Mana team's platform admin, whose token
// opens only /backoffice. The role is kept beside the token so a reload lands
// on the right screens without asking the server.

const TOKEN_KEY = "mana_dash_token";
const ROLE_KEY = "mana_dash_role";

export const PLATFORM_ADMIN = "platform_admin";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function getRole(): string | null {
  return localStorage.getItem(ROLE_KEY);
}
export function setSession(token: string, role: string): void {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(ROLE_KEY, role);
}
export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(ROLE_KEY);
}

function authHeaders(): HeadersInit {
  const h: Record<string, string> = { "Content-Type": "application/json" };
  const tok = getToken();
  if (tok) h["Authorization"] = `Bearer ${tok}`;
  return h;
}

export class APIError extends Error {
  status: number;
  /** Machine-readable refusal from the backoffice, translated by the screen. */
  reason?: string;
  constructor(status: number, message: string, reason?: string) {
    super(message);
    this.status = status;
    this.reason = reason;
  }
}

async function asJSON<T>(res: Response): Promise<T> {
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const b = body as { error?: string; reason?: string };
    throw new APIError(res.status, b.error ?? `HTTP ${res.status}`, b.reason);
  }
  return body as T;
}

export interface User {
  id: string;
  email: string;
  tenant_id: string;
  role: string;
}

export function login(email: string, password: string): Promise<{ token: string; user: User }> {
  return fetch(`/api/v1/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  }).then(asJSON<{ token: string; user: User }>);
}

export interface Recipient {
  id: string;
  kind: string;
  display_name: string;
  payout_ref?: string;
}

export function listRecipients(): Promise<{ recipients: Recipient[] }> {
  return fetch(`/api/v1/dashboard/recipients`, { headers: authHeaders() }).then(asJSON<{ recipients: Recipient[] }>);
}
export function createRecipient(kind: string, displayName: string): Promise<Recipient> {
  return fetch(`/api/v1/dashboard/recipients`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ kind, display_name: displayName }),
  }).then(asJSON<Recipient>);
}

export interface TipPage {
  id: string;
  slug: string;
  recipient_id: string;
  currency: string;
  preset_amounts: number[];
  is_active: boolean;
}

export function listTipPages(): Promise<{ tip_pages: TipPage[] }> {
  return fetch(`/api/v1/dashboard/tip-pages`, { headers: authHeaders() }).then(asJSON<{ tip_pages: TipPage[] }>);
}
export function createTipPage(input: {
  recipient_id: string;
  currency: string;
  preset_amounts: number[];
  message_i18n: Record<string, string>;
}): Promise<TipPage> {
  return fetch(`/api/v1/dashboard/tip-pages`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(input),
  }).then(asJSON<TipPage>);
}

export interface TipPageLinks {
  slug: string;
  tip_url: string;
  qr_data_url: string;
  telegram_deep_link?: string;
  mini_app_link?: string;
}
export function tipPageLinks(id: string): Promise<TipPageLinks> {
  return fetch(`/api/v1/dashboard/tip-pages/${id}/links`, { headers: authHeaders() }).then(asJSON<TipPageLinks>);
}

export interface Stats {
  tips: { currency: string; count: number; gross_minor: number; fee_minor: number }[];
  outstanding: { currency: string; amount: number }[];
}
export function getStats(): Promise<Stats> {
  return fetch(`/api/v1/dashboard/stats`, { headers: authHeaders() }).then(asJSON<Stats>);
}

export function createPayout(recipientID: string, currency: string): Promise<unknown> {
  return fetch(`/api/v1/dashboard/payouts`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ recipient_id: recipientID, currency }),
  }).then(asJSON);
}

export function createBadge(code: string, metric: string, threshold: number, title: Record<string, string>): Promise<unknown> {
  return fetch(`/api/v1/dashboard/badges`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ code, title_i18n: title, criteria: { metric, threshold } }),
  }).then(asJSON);
}

// ── Backoffice (platform admin) ─────────────────────────────────────────────

export interface BoVenue {
  id: string;
  name: string;
  neighborhood: string;
  created_at?: string;
  live_codes: number;
  staff: number;
  protected: boolean;
  /** Nobody works there, no tables, no tips — the only venues that can be deleted. */
  deletable: boolean;
}

export type BoCodeStatus = "active" | "revoked" | "expired" | "full";

export interface BoCode {
  code: string;
  venue: BoVenue;
  uses: number;
  max_uses: number | null;
  status: BoCodeStatus;
  personal: boolean;
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

export type BoMemberStatus = "confirmed" | "pending" | "closed";

export interface BoMember {
  name: string;
  email: string;
  venue: BoVenue;
  code: string;
  status: BoMemberStatus;
  signed_up_at: string;
  confirmed_at: string | null;
}

export interface BoIssued {
  code: string;
  venue: BoVenue;
  venue_created: boolean;
  existing: boolean;
  neighborhood_ignored: boolean;
  uses: number;
  max_uses: number | null;
}

export interface BoIssueInput {
  venue_id?: string;
  venue?: string;
  neighborhood?: string;
  code?: string;
  max_uses?: number;
}

export function listBoVenues(): Promise<{ venues: BoVenue[] }> {
  return fetch(`/api/v1/backoffice/venues`, { headers: authHeaders() }).then(asJSON<{ venues: BoVenue[] }>);
}
export function createBoVenue(name: string, neighborhood: string): Promise<BoVenue> {
  return fetch(`/api/v1/backoffice/venues`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ name, neighborhood }),
  }).then(asJSON<BoVenue>);
}
export function deleteBoVenue(id: string): Promise<{ name: string; codes_removed: number }> {
  return fetch(`/api/v1/backoffice/venues/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: authHeaders(),
  }).then(asJSON<{ name: string; codes_removed: number }>);
}
export function renameBoVenue(id: string, name: string, neighborhood: string): Promise<BoVenue> {
  return fetch(`/api/v1/backoffice/venues/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: authHeaders(),
    body: JSON.stringify({ name, neighborhood }),
  }).then(asJSON<BoVenue>);
}
export function listBoCodes(): Promise<{ codes: BoCode[] }> {
  return fetch(`/api/v1/backoffice/codes`, { headers: authHeaders() }).then(asJSON<{ codes: BoCode[] }>);
}
export function issueBoCode(input: BoIssueInput): Promise<BoIssued> {
  return fetch(`/api/v1/backoffice/codes`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(input),
  }).then(asJSON<BoIssued>);
}
export function revokeBoCode(code: string): Promise<{ code: string; already_revoked_at: string | null }> {
  return fetch(`/api/v1/backoffice/codes/${encodeURIComponent(code)}/revoke`, {
    method: "POST",
    headers: authHeaders(),
  }).then(asJSON<{ code: string; already_revoked_at: string | null }>);
}
export function listBoStaff(): Promise<{ staff: BoMember[] }> {
  return fetch(`/api/v1/backoffice/staff`, { headers: authHeaders() }).then(asJSON<{ staff: BoMember[] }>);
}
